import type {
  AvailabilityCommandContext,
  AvailabilityProjection,
  AvailabilityReasonCode,
  createAvailabilityAuthority,
} from '../availability/availability-authority.ts';
import type { BusinessPolicySnapshot } from '../policy/business-policy.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type {
  SmtAvailabilityDispatchResult,
  SmtAvailabilityIntent,
  SmtAvailabilityMutationContext,
  SmtAvailabilityNodeKind,
  SmtAvailabilityNodeViewModel,
  SmtAvailabilityRequest,
  SmtAvailabilityViewModel,
} from './smt-availability-contract.ts';

type AvailabilityAuthority = ReturnType<typeof createAvailabilityAuthority>;

export type SmtAvailabilityPort = Pick<
  AvailabilityAuthority,
  'projection' | 'setNodes' | 'createRestoreSchedule' | 'updateRestoreSchedule' | 'cancelRestoreSchedule'
>;

export interface SmtAvailabilityStaffAuthorizationRequest {
  readonly actorId: string;
  readonly permission: Extract<StaffPermission, 'availability.change' | 'availability.schedule'>;
  readonly at: string;
}

export interface CreateSmtAvailabilitySurfaceDependencies {
  readonly getAvailabilityAuthority: () => SmtAvailabilityPort | null;
  readonly getPolicy: () => BusinessPolicySnapshot | null;
  readonly authorizeStaff?: (request: SmtAvailabilityStaffAuthorizationRequest) => void;
}

const assertRequest = (request: SmtAvailabilityRequest): void => {
  if (!request.actorId?.trim()) throw new Error('SMT_AVAILABILITY_ACTOR_REQUIRED');
  if (!Number.isFinite(Date.parse(request.at))) throw new Error('SMT_AVAILABILITY_TIMESTAMP_INVALID');
};

const requireAuthority = (deps: CreateSmtAvailabilitySurfaceDependencies): SmtAvailabilityPort => {
  const authority = deps.getAvailabilityAuthority();
  if (!authority) throw new Error('SMT_AVAILABILITY_AUTHORITY_REQUIRED');
  return authority;
};

const requirePolicy = (deps: CreateSmtAvailabilitySurfaceDependencies): BusinessPolicySnapshot => {
  const policy = deps.getPolicy();
  if (!policy) throw new Error('SMT_AVAILABILITY_POLICY_REQUIRED');
  return policy;
};

const nodeKindOf = (nodeId: string): SmtAvailabilityNodeKind => {
  if (nodeId.startsWith('product:')) return 'product';
  if (nodeId.startsWith('ingredient:')) return 'ingredient';
  return 'other';
};

const productIdOf = (nodeId: string): string | undefined => {
  if (!nodeId.startsWith('product:')) return undefined;
  const productId = nodeId.slice('product:'.length).trim();
  return productId || undefined;
};

const affectedProductsFor = (nodeId: string, policy: BusinessPolicySnapshot): string[] =>
  Object.entries(policy.availability.productRequirements)
    .filter(([, nodeIds]) => nodeIds?.includes(nodeId) === true)
    .map(([productId]) => productId)
    .sort();

const permissionHint = (
  deps: CreateSmtAvailabilitySurfaceDependencies,
  request: SmtAvailabilityRequest,
  permission: SmtAvailabilityStaffAuthorizationRequest['permission'],
): boolean => {
  if (!deps.authorizeStaff) return false;
  try {
    deps.authorizeStaff({ actorId: request.actorId, permission, at: request.at });
    return true;
  } catch {
    return false;
  }
};

const toA3Context = (context: SmtAvailabilityMutationContext): AvailabilityCommandContext => ({
  actorId: context.actorId,
  sourceSurface: 'smt',
  at: context.at,
  expectedRevision: context.expectedRevision,
  idempotencyKey: context.idempotencyKey,
});

const knownBusinessErrorCode = (error: unknown): string | null => {
  if (!(error instanceof Error)) return null;
  if (error.message.startsWith('AVAILABILITY_')) return error.message;
  if (error.message.startsWith('STAFF_')) return error.message;
  return null;
};

const manualMapping = (
  kind: Extract<
    SmtAvailabilityIntent['kind'],
    'availability.mark-soldout' | 'availability.restore' | 'availability.pause' | 'availability.resume'
  >,
): { readonly status: 'available' | 'soldout' | 'paused'; readonly reasonCode: AvailabilityReasonCode } => {
  switch (kind) {
    case 'availability.mark-soldout':
      return { status: 'soldout', reasonCode: 'manual_soldout' };
    case 'availability.restore':
      return { status: 'available', reasonCode: 'manual_restore' };
    case 'availability.pause':
      return { status: 'paused', reasonCode: 'production_pause' };
    case 'availability.resume':
      return { status: 'available', reasonCode: 'production_resume' };
  }
};

const project = (
  projection: AvailabilityProjection,
  policy: BusinessPolicySnapshot,
  request: SmtAvailabilityRequest,
  deps: CreateSmtAvailabilitySurfaceDependencies,
): SmtAvailabilityViewModel => {
  const nodes: SmtAvailabilityNodeViewModel[] = projection.nodes.map((node) => {
    const productId = productIdOf(node.nodeId);
    return {
      ...structuredClone(node),
      nodeKind: nodeKindOf(node.nodeId),
      ...(productId ? { productId } : {}),
      affectedProductIds: affectedProductsFor(node.nodeId, policy),
    };
  });

  const statusByNode = new Map(nodes.map((node) => [node.nodeId, node.status] as const));
  const affectedProductsUnavailable = Object.entries(policy.availability.productRequirements)
    .filter(([, requirements]) => requirements?.some((nodeId) => statusByNode.get(nodeId) !== 'available') === true)
    .length;

  return {
    storeId: projection.storeId,
    revision: projection.revision,
    availabilityRevision: projection.revision,
    summary: {
      totalNodes: nodes.length,
      soldoutNodes: nodes.filter((node) => node.status === 'soldout').length,
      pausedNodes: nodes.filter((node) => node.status === 'paused').length,
      affectedProductsUnavailable,
    },
    permissions: {
      canChangeAvailability: permissionHint(deps, request, 'availability.change'),
      canScheduleRestore: permissionHint(deps, request, 'availability.schedule'),
    },
    nodes,
    schedules: structuredClone(projection.schedules),
    dailySoldoutRestorePolicy: structuredClone(projection.dailySoldoutRestorePolicy),
  };
};

export function createSmtAvailabilitySurface(deps: CreateSmtAvailabilitySurfaceDependencies) {
  const snapshot = (request: SmtAvailabilityRequest): SmtAvailabilityViewModel => {
    assertRequest(request);
    const authority = requireAuthority(deps);
    const policy = requirePolicy(deps);
    return project(authority.projection(), policy, request, deps);
  };

  const dispatch = (
    intent: SmtAvailabilityIntent,
    context: SmtAvailabilityMutationContext,
  ): SmtAvailabilityDispatchResult => {
    assertRequest(context);
    const authority = requireAuthority(deps);
    const policy = requirePolicy(deps);
    const a3Context = toA3Context(context);

    try {
      switch (intent.kind) {
        case 'availability.mark-soldout':
        case 'availability.restore':
        case 'availability.pause':
        case 'availability.resume': {
          const mapping = manualMapping(intent.kind);
          authority.setNodes({
            nodeIds: intent.nodeIds,
            status: mapping.status,
            reasonCode: mapping.reasonCode,
            ...(intent.reasonNote !== undefined ? { reasonNote: intent.reasonNote } : {}),
          }, a3Context);
          break;
        }
        case 'availability.restore-schedule.create':
          authority.createRestoreSchedule({
            scheduleId: intent.scheduleId,
            nodeIds: intent.nodeIds,
            restoreAt: intent.restoreAt,
          }, a3Context);
          break;
        case 'availability.restore-schedule.update':
          authority.updateRestoreSchedule({
            scheduleId: intent.scheduleId,
            nodeIds: intent.nodeIds,
            restoreAt: intent.restoreAt,
          }, a3Context);
          break;
        case 'availability.restore-schedule.cancel':
          authority.cancelRestoreSchedule({ scheduleId: intent.scheduleId }, a3Context);
          break;
        default: {
          const exhaustive: never = intent;
          throw new Error(`SMT_AVAILABILITY_INTENT_UNSUPPORTED:${String(exhaustive)}`);
        }
      }
    } catch (error: unknown) {
      const code = knownBusinessErrorCode(error);
      if (!code) throw error;
      return {
        kind: 'rejected',
        code,
        snapshot: project(authority.projection(), policy, context, deps),
      };
    }

    return {
      kind: 'accepted',
      snapshot: project(authority.projection(), policy, context, deps),
    };
  };

  return { snapshot, dispatch };
}
